import { useCallback, useId, useState } from 'react';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { TooltipOptions } from 'primereact/tooltip/tooltipoptions';
import { Combobox, GetItemsProps } from 'medere-ui-framework';
import { AppointmentPractice } from '../types/agenda.types';
import { getRequestingDoctors } from '../services/AgendaService';
import { getProfessionalActivities } from '../../../servicesHooks/Budget/useBudgetService';
import { NomenclatorDTO } from '../../../dto/NomenclatorDTO';
import { ComboDataDTO } from '../../../dto/ComboDataDTO';
import { FieldError } from '../../../components/novarium/NvTableParts';
import NewRequestingDoctorModal from './NewRequestingDoctorModal';
import './PracticesEditor.scss';

const NEW_REQUESTING_DOCTOR_ID = -1;

const TOOLTIP_OPTIONS: TooltipOptions = { className: 'nv-tooltip', event: 'both', position: 'top' };

interface PracticesEditorProps {
  practices: AppointmentPractice[];
  onChange: (practices: AppointmentPractice[]) => void;
  onError?: (message: string) => void;
}

function formatDateForApi(date: Date | null): string | null {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** "2026-05-01" → "1/5/2026", el formato de fecha del design system. */
function formatDateForDisplay(apiDate: string): string {
  const [year, month, day] = apiDate.split('-').map(Number);
  return year && month && day ? `${day}/${month}/${year}` : apiDate;
}

export default function PracticesEditor({ practices, onChange, onError }: PracticesEditorProps) {
  const idPrefix = useId();
  const [selectedPractice, setSelectedPractice] = useState<NomenclatorDTO | null>(null);
  const [practiceDoctor, setPracticeDoctor] = useState<ComboDataDTO | null>(null);
  const [practiceDate, setPracticeDate] = useState<Date | null>(new Date());
  const [practiceObservations, setPracticeObservations] = useState('');
  const [practiceError, setPracticeError] = useState<string | undefined>(undefined);
  const [newRequestingDoctorVisible, setNewRequestingDoctorVisible] = useState(false);

  const getRequestingDoctorOptions = useCallback(async (props: GetItemsProps) => {
    const doctors = await getRequestingDoctors(props);
    if ((props.offSet ?? 0) === 0) {
      return [{ id: NEW_REQUESTING_DOCTOR_ID, name: 'Nuevo médico solicitante' }, ...doctors];
    }
    return doctors;
  }, []);

  const handleRequestingDoctorChange = useCallback((value: ComboDataDTO | null) => {
    if (value?.id === NEW_REQUESTING_DOCTOR_ID) {
      setNewRequestingDoctorVisible(true);
      return;
    }
    setPracticeDoctor(value);
  }, []);

  // "Agregar práctica" siempre habilitado: si falta la práctica o ya está cargada, se marca el campo y no se agrega.
  const addPractice = useCallback(() => {
    if (!selectedPractice?.nomenclator) {
      setPracticeError('Elegí una práctica');
      return;
    }
    const nomenclatorId = Number(selectedPractice.nomenclator);
    if (practices.some((p) => p.nomenclatorId === nomenclatorId)) {
      setPracticeError('Elegí otra práctica: esta ya está cargada');
      return;
    }

    onChange([...practices, {
      nomenclatorId,
      code: selectedPractice.code ?? '',
      description: selectedPractice.description ?? selectedPractice.codeanddescription ?? '',
      requestingDoctorId: practiceDoctor?.id ?? null,
      requestingDoctorName: practiceDoctor?.name ?? null,
      practiceDate: formatDateForApi(practiceDate),
      observations: practiceObservations,
    }]);
    setSelectedPractice(null);
    setPracticeDoctor(null);
    setPracticeObservations('');
  }, [practiceDate, practiceDoctor, practiceObservations, practices, selectedPractice, onChange]);

  const removePractice = useCallback((index: number) => {
    onChange(practices.filter((_, i) => i !== index));
    // Si la marca era "ya está cargada", al quitar la práctica deja de valer
    setPracticeError(undefined);
  }, [practices, onChange]);

  const dateId = `${idPrefix}-date`;
  const doctorId = `${idPrefix}-doctor`;
  const practiceId = `${idPrefix}-practice`;
  const observationsId = `${idPrefix}-observations`;

  return (
    <div className="practices-editor">
      <div className="practices-editor__fields">
        <div className="nv-field">
          <label htmlFor={dateId}>Fecha</label>
          <Calendar
            inputId={dateId}
            value={practiceDate}
            onChange={(e) => setPracticeDate(e.value as Date | null)}
            dateFormat="dd/mm/yy"
            showIcon
            panelClassName="nv-scope"
          />
        </div>
        <div className="nv-field">
          <label htmlFor={doctorId}>Profesional solicitante</label>
          {/* TODO(design-system): el Combobox de medere-ui-framework no acepta panelClassName, así que su panel no
              lleva nv-scope y conserva el look del framework. */}
          <Combobox
            inputId={doctorId}
            getItems={getRequestingDoctorOptions}
            optionLabel="name"
            itemTemplate={(option: ComboDataDTO) => (option.id === NEW_REQUESTING_DOCTOR_ID ? (
              <span className="nv-scope practices-editor__new-doctor">
                <i className="pi pi-plus-circle" aria-hidden />
                {option.name}
              </span>
            ) : (
              <span>{option.name}</span>
            ))}
            placeholder="Seleccionar"
            setValue={(value: ComboDataDTO | null) => handleRequestingDoctorChange(value)}
            value={practiceDoctor?.name ?? ''}
          />
        </div>
        <div className="nv-field">
          <label htmlFor={practiceId}>Práctica <span className="nv-req">*</span></label>
          <Combobox
            inputId={practiceId}
            getItems={getProfessionalActivities}
            params={{ healthEntity: -1, orderByNomenclatorType: true }}
            optionLabel="codeanddescription"
            placeholder="Buscar por código o descripción"
            minPanelWidth={500}
            setValue={(value: NomenclatorDTO) => {
              setSelectedPractice(value ?? null);
              setPracticeError(undefined);
            }}
            value={selectedPractice?.codeanddescription ?? ''}
            error={Boolean(practiceError)}
          />
          <FieldError id={`${practiceId}-error`} message={practiceError} />
        </div>
        <div className="practices-editor__bottom-row">
          <div className="nv-field">
            <label htmlFor={observationsId}>Observaciones</label>
            <InputText
              id={observationsId}
              value={practiceObservations}
              onChange={(e) => setPracticeObservations(e.target.value)}
            />
          </div>
          <div className="nv-field-inline-action">
            <Button
              type="button"
              className="buttonMain2"
              outlined
              icon="pi pi-plus-circle"
              label="Agregar práctica"
              onClick={addPractice}
            />
          </div>
        </div>
      </div>
      {practices.length === 0 ? (
        <p className="practices-editor__list practices-editor__empty">Sin prácticas cargadas</p>
      ) : (
        <ul className="practices-editor__list" aria-label="Prácticas cargadas">
          {practices.map((practice, index) => {
            const details = [
              practice.practiceDate ? `Fecha: ${formatDateForDisplay(practice.practiceDate)}` : null,
              practice.requestingDoctorName ? `Solicitante: ${practice.requestingDoctorName}` : null,
              practice.observations || null,
            ].filter(Boolean).join(' · ');
            return (
              <li className="practices-editor__row" key={`${practice.id ?? practice.nomenclatorId}-${index}`}>
                <div className="nv-stack-4">
                  <span><span className="nv-strong">{practice.code}</span> · {practice.description}</span>
                  {details && <span className="nv-help">{details}</span>}
                </div>
                <Button
                  type="button"
                  text
                  className="nv-icon"
                  icon="pi pi-trash"
                  aria-label="Quitar práctica"
                  tooltip="Quitar práctica"
                  tooltipOptions={TOOLTIP_OPTIONS}
                  onClick={() => removePractice(index)}
                />
              </li>
            );
          })}
        </ul>
      )}
      <NewRequestingDoctorModal
        visible={newRequestingDoctorVisible}
        onHide={() => setNewRequestingDoctorVisible(false)}
        onCreated={(doctor) => setPracticeDoctor(doctor)}
        onError={onError}
      />
    </div>
  );
}
